import { Navigate, Outlet } from "react-router-dom";
import { useAuth, FullPageLoader } from "@livingsyncs/ui";
import { usePortalGuard } from "@livingsyncs/ui/src/lib/crossAppRedirect";
import { resolveHomePortal } from "@livingsyncs/permissions";

/**
 * Portal-membership boundary for a standalone, single-portal app (frontend
 * split epic Phase 6) — mirror of admin's own `AdminBoundary.tsx`, "admin"
 * swapped for "audit". A signed-in user whose home portal isn't "audit" is
 * signed out (usePortalGuard) and sent to /login with an inline error
 * naming the correct portal — never rendered/redirected into it, whether
 * that mismatch shows up on a fresh sign-in or on an existing session (e.g.
 * after a role change).
 *
 * "no-access" renders this app's own local /no-access route.
 *
 * The loading guard is load-bearing (same as PortalBoundary's own note):
 * zero access decision while useAuth is unresolved.
 */
export function AuditorBoundary() {
  const { data: context, isLoading } = useAuth();
  const home = context ? resolveHomePortal(context) : null;
  const status = usePortalGuard(home, "audit");

  if (isLoading || !context || status === "checking") {
    return <FullPageLoader />;
  }

  if (status === "wrong-portal") {
    return <Navigate to="/login" state={{ wrongPortalHome: home }} replace />;
  }

  if (status === "no-access") {
    return <Navigate to="/no-access" replace />;
  }

  return <Outlet />;
}
